/**
 * JsonColumnToggle — Table Designer column-property affordance that
 * declares / un-declares a string column as JSON via the shared
 * `jsonColumns` MetaInfo store (the SAME store the rest of the app reads
 * for JSON-aware autocomplete / classification — JCS-2 loose-json-declared).
 *
 * The toggle does NOT emit DDL; it writes a MetaInfo declaration only. On
 * both directions it invalidates the TanStack MetaInfo cache so the toggle's
 * own ON/OFF state reflects the write without a reload. The shared
 * autocomplete hint provider is refreshed automatically by the
 * onMetaInfoWritten IPC bridge (see metaInfoHintWrittenBridge.ts); call
 * sites no longer need to invoke invalidateHintProvider manually.
 *
 * Plan: § Phase 7 — Table Designer "Treat as JSON column" toggle.
 */
import { useCallback, useMemo, useState } from 'react';

import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAnalytics } from '../../../../core/analytics';
import {
  addJsonColumn,
  removeJsonColumn,
} from '../../common/services/metaInfoApi';
import {
  useMetaInfo,
  useInvalidateMetaInfo,
} from '../../common/hooks/useMetaInfo';
import {
  categorizeJsonColumn,
  type JsonColumnInput,
} from '../../common/utils/jsonColumnCategorization';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export type JsonColumnToggleProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly table: string;
  readonly column: string;
  /** Engine-specific column data-type string, e.g. `nvarchar(max)`. */
  readonly dataType: string;
  readonly engine: DatabaseEngineId;
  readonly testId?: string;
};

const newDeclarationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `json-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Mirrors GlobalDeclareJsonColumnDialog's `friendlyDeclareError`: surface the
// underlying message when present, else a generic actionable fallback.
const friendlyToggleError = (err: unknown): string =>
  err instanceof Error && err.message
    ? err.message
    : 'Failed to update the JSON declaration. Check your connection and try again.';

const DECL_NOT_FOUND_MESSAGE =
  "Couldn't find the JSON declaration to remove — refresh and try again.";

export function JsonColumnToggle({
  connectionId,
  database,
  schema,
  table,
  column,
  dataType,
  engine,
  testId = 'json-column-toggle',
}: JsonColumnToggleProps) {
  const { track } = useAnalytics();
  const invalidateMetaInfoCache = useInvalidateMetaInfo();
  const metaInfoQuery = useMetaInfo(connectionId, database);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const input: JsonColumnInput = useMemo(
    () => ({ engine, schema, table, name: column, dataType }),
    [engine, schema, table, column, dataType],
  );

  const isDeclared =
    categorizeJsonColumn(input, metaInfoQuery.data) === 'declared-json';

  const handleToggle = useCallback(
    async (next: boolean) => {
      if (busy) return;
      setBusy(true);
      setErrorMessage(null);
      try {
        if (next) {
          await addJsonColumn(connectionId, database, {
            id: newDeclarationId(),
            schema,
            table,
            column,
            createdAt: new Date().toISOString(),
          });
          track('feature:table_designer_json_toggle', { enabled: true });
        } else {
          const existing = (metaInfoQuery.data?.jsonColumns ?? []).find(
            (d) =>
              d.schema === schema &&
              d.table === table &&
              d.column === column,
          );
          if (!existing) {
            // Stale cache / key drift: the toggle reads ON (categorization
            // matched) but the declaration list lacks the row. Do NOT fire
            // analytics or imply success — surface the miss and still
            // invalidate so the next render reflects true state.
            setErrorMessage(DECL_NOT_FOUND_MESSAGE);
            invalidateMetaInfoCache(connectionId, database);
            return;
          }
          await removeJsonColumn(connectionId, database, existing.id);
          track('feature:table_designer_json_toggle', { enabled: false });
        }
        invalidateMetaInfoCache(connectionId, database);
      } catch (err) {
        setErrorMessage(friendlyToggleError(err));
      } finally {
        setBusy(false);
      }
    },
    [
      busy,
      connectionId,
      database,
      schema,
      table,
      column,
      metaInfoQuery.data,
      track,
      invalidateMetaInfoCache,
    ],
  );

  const labelId = `${testId}-label`;

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      {/* F23 — whitespace-nowrap + a shorter label keep this row from
          wrapping to two lines in the narrow column-property panel, which
          broke row height parity with its neighbors. The switch's accessible
          name still comes from this label via aria-labelledby. */}
      <div className="flex items-center gap-2 whitespace-nowrap">
        <Switch
          checked={isDeclared}
          disabled={busy}
          onCheckedChange={handleToggle}
          aria-labelledby={labelId}
          data-testid={`${testId}-switch`}
        />
        <Label id={labelId} className="text-xs text-muted-foreground whitespace-nowrap">
          JSON column
        </Label>
      </div>
      {errorMessage ? (
        <p
          role="alert"
          className="text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
